import { useEffect, useRef, useState } from 'react';

import { Alert, Box } from '@mui/material';

import { PageLoader } from '@/components/common';
import { logger } from '@/utils/logger';

import { useGetAnalyticsDashboardQuery } from '../api/analyticsApi';

// Declare global QuickSightEmbedding from script tag
declare global {
  interface Window {
    QuickSightEmbedding: {
      embedDashboard: (options: {
        url: string;
        container: HTMLElement;
        scrolling: string;
        locale: string;
      }) => {
        on: (event: string, callback: () => void) => void;
      };
    };
  }
}

interface AnalyticsOverviewProps {
  tenantId: string;
}

export const AnalyticsOverview = ({ tenantId }: AnalyticsOverviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [embeddingFailed, setEmbeddingFailed] = useState(false);
  const dashboardRef = useRef<unknown>(null);

  const {
    data: embedUrl,
    isLoading,
    isError,
  } = useGetAnalyticsDashboardQuery(tenantId, {
    skip: !tenantId,
  });

  useEffect(() => {
    if (!embedUrl || !containerRef.current) {
      return;
    }

    const container = containerRef.current;

    // Clean up previous dashboard if it exists
    if (dashboardRef.current) {
      container.innerHTML = '';
    }

    // Wait for QuickSightEmbedding to be available from script tag
    const checkAndEmbed = () => {
      if (!window.QuickSightEmbedding) {
        // SDK not loaded yet, wait a bit
        setTimeout(checkAndEmbed, 100);
        return;
      }

      try {
        const options = {
          url: embedUrl,
          container,
          scrolling: 'yes',
          locale: 'en-US',
        };

        const dashboard = window.QuickSightEmbedding.embedDashboard(options);
        dashboardRef.current = dashboard;

        dashboard.on('error', () => {
          setEmbeddingFailed(true);
        });
      } catch (error) {
        logger.error('Failed to embed dashboard:', error);
        setEmbeddingFailed(true);
      }
    };

    checkAndEmbed();

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [embedUrl]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <PageLoader variant="section" message="Loading analytics dashboard..." />
      </Box>
    );
  }

  if (isError || embeddingFailed) {
    return (
      <Alert severity="error">
        Your dashboard failed to load. Please contact Lingk support via live chat or
        support@lingk.io
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 250px)', // Account for header, padding, and page heading
      }}
    >
      <div
        ref={containerRef}
        style={{
          height: '100%',
          width: '100%',
        }}
      />
    </Box>
  );
};
